import type { ReactNode } from "react"
import { X } from "lucide-react"

import { Modal } from "../ui/modal"

export type EnlargedProductVisual = {
  url: string | null
  alt: string
  caption: string
}

// A product visual is a 1200×1200 artifact; the gallery cell is a thumbnail, so
// enlarging is the only way to actually read one.
//
// `Modal` owns the dialog behaviour — role, focus-in and focus-return, the Tab
// trap, Escape, the click-to-dismiss backdrop. This owns only what is specific
// to a visual: the caption that names it and the image itself.
export function ProductVisualLightbox({
  image,
  details,
  onClose,
}: {
  image: EnlargedProductVisual
  details?: ReactNode
  onClose: () => void
}) {
  return (
    <Modal
      label={image.caption}
      onClose={onClose}
      surfaceClassName={details ? "max-w-6xl" : "max-w-3xl"}
    >
      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <span className="truncate text-[13px] text-foreground">{image.caption}</span>

        <button
          type="button"
          aria-label="Close enlarged visual"
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
          onClick={onClose}
        >
          <X className="size-4" />
        </button>
      </div>

      {details ? (
        <div className="grid min-h-0 flex-1 overflow-y-auto border-t border-border md:grid-cols-[minmax(0,1.6fr)_minmax(18rem,0.8fr)] md:overflow-hidden">
          <VisualArtwork image={image} />

          {details}
        </div>
      ) : (
        <VisualArtwork image={image} />
      )}
    </Modal>
  )
}

function VisualArtwork({ image }: { image: EnlargedProductVisual }) {
  return (
    <div className="flex min-h-64 items-center justify-center bg-muted/40 p-3 sm:p-5 md:min-h-0">
      {image.url ? (
        <img src={image.url} alt={image.alt} className="max-h-full w-full object-contain" />
      ) : (
        <p className="max-w-xs text-center text-sm text-muted-foreground">
          No published image for this attempt.
        </p>
      )}
    </div>
  )
}
